// Wildfire overlay — NASA EONET active-fire events as MIL-STD-2525-style
// icons, severity-scaled (EONET magnitude → `severity` by the connector).
//
// Mirrors seismic.ts: a geophysical/disaster event class that is regional /
// global news, so it's visible from country zoom upward. Fire reads orange
// (--amber); the per-marker fill still ramps by severity via buildIconLayer,
// so a small smoulder and a megafire are visually distinct.

import type { intel as i } from '@eidollon/contracts';
import { buildIconLayer } from './_icon-builder.js';
import { withGeom } from './_shared.js';
import type { LayerSpec } from './registry.js';

const TYPES: readonly i.SignalType[] = ['FIR'];
const TYPE_SET = new Set<i.SignalType>(TYPES);

export const wildfireLayer: LayerSpec = {
  id: 'wildfire',
  label: 'FIR · wildfire',
  kind: 'icon',
  signalTypes: TYPES,
  color: 'var(--amber)',
  colorAxis: 'severity',
  description: 'NASA EONET active wildfires, severity-scaled',
  classification: 'OPEN',
  legend: {
    swatch: 'var(--amber)',
    label: 'FIR',
    hint: 'NASA EONET active wildfires',
  },
  select: (rows) => withGeom(rows).filter((r) => TYPE_SET.has(r.signal_type)),
  // Visible from country zoom upward — large wildfires are regional / global
  // news, same wide-context treatment as seismic.
  // Always visible — the feed is capped at ~200 rows so there is no clutter,
  // and a single-type filter must show that type at ANY zoom (otherwise the
  // map goes empty below the old threshold). See route bug: "toggle on -> disappear".
  visibleAtZoom: () => true,
  deckLayer: ({ rows, viewport, onPick }) => {
    const data = withGeom(rows).filter((r) => TYPE_SET.has(r.signal_type));
    return buildIconLayer({
      id: 'intel-wildfire',
      rows: data,
      zoom: viewport.zoom,
      sizeMin: 8,
      sizeMax: 28,
      onPick,
    });
  },
};
